/**
 * 場後結算 · 逐項輸入列（P1-10；`ui-spec` 頁 12）
 *
 * 每項要填齊「賣出／剩貨／損壞」（**箱 ＋ 散件**）；有剩貨就要揀去向（`G2`），
 * 損毀註銷要填原因（`G5`）。畫面即時顯示對數結果（差幾多件）。
 *
 * 為何抽離（硬規則 B：頁 ≤200 行、元件 ≤250 行）：一行有 6 個數量格 ＋ 去向／目標／備註，
 * 擺入表單檔一定爆；「格 ↔ 模型」搬字過紙搬去 `../lib/reconcile` 共用。
 *
 * 紅線（前端只係提示；**後端 ＋ 模組才係權威**）
 *   · ⚠️ 對數（`G1`）**一律**用 `@coeldery/event-reconciliation` 嘅 `lineErrors()`／`toUnits()`／
 *     `DISPOSITION_RULES` —— 前端**唔准**自己再寫一份（R1：規則只得一個實作）
 *   · ⚠️ 落點（`roadshow:<單號>`／`store:<code>`…）**唔准**自己駁字串：預覽用模組
 *     `dispositionLocation()`，存檔後一律以後端回嘅 `toLoc` 為準
 *   · ⚠️ 去向清單唔准寫死：由 API 出（`destinations` 表）
 */
import { useTranslation } from 'react-i18next';
import FormSelectField from '@coeldery/form-select-field';
import FormTextField from '@coeldery/form-text-field';
import { errText, grid2, hint as hintStyle } from '@coeldery/ui-kit';
import {
  DISPOSITION_RULES,
  dispositionLocation,
  hasQty,
  isDispositionCode,
  lineErrors,
  toUnits,
  type ReconcileLineContext,
} from '@coeldery/event-reconciliation';
import type { LabelOption, ReconcileBaseLine, RoadshowTarget } from '../lib/api';
import { rowToLine, type RowState } from '../lib/reconcile';
import { optionLabel } from './OutboundStatusChip';

type Group = 'sold' | 'leftover' | 'damaged';

/** 一組數量（箱 ＋ 件）—— 三組（賣出／剩貨／損壞）共用同一排版 */
function QtyGroup({
  titleKey,
  lineId,
  group,
  row,
  onChange,
  readOnly,
}: {
  titleKey: string;
  lineId: string;
  group: Group;
  row: RowState;
  onChange: (patch: Partial<RowState>) => void;
  readOnly: boolean;
}) {
  const { t } = useTranslation();
  const field = (suffix: 'cartons' | 'units', labelKey: string) => {
    const key = `${group}${suffix === 'cartons' ? 'Cartons' : 'Units'}` as keyof RowState;
    return (
      <FormTextField
        id={`f-rc-${group}-${suffix}-${lineId}`}
        labelKey={labelKey}
        type="number"
        value={row[key]}
        readOnly={readOnly}
        onChange={(v) => onChange({ [key]: v ?? '' } as Partial<RowState>)}
      />
    );
  };
  return (
    <div style={{ borderTop: '1px solid var(--color-divider)', paddingTop: '6px' }}>
      <p style={{ margin: 0, fontSize: '14px', fontWeight: 700 }}>{t(titleKey)}</p>
      <div style={grid2}>
        {field('cartons', 'recon.cartons')}
        {field('units', 'recon.units')}
      </div>
    </div>
  );
}

export interface ReconcileRowProps {
  base: ReconcileBaseLine;
  ctx: ReconcileLineContext;
  row: RowState;
  onChange: (patch: Partial<RowState>) => void;
  /** 已確認 ＝ 唯讀（append-only；要更正另開調整） */
  readOnly: boolean;
  dispositions: LabelOption[];
  roadshowTargets: RoadshowTarget[];
  storeTargets: LabelOption[];
  lang: string;
}


export default function ReconcileRow({
  base,
  ctx,
  row,
  onChange,
  readOnly,
  dispositions,
  roadshowTargets,
  storeTargets,
  lang,
}: ReconcileRowProps) {
  const { t } = useTranslation();
  const model = rowToLine(base, row);
  const issues = lineErrors(model, ctx);
  const upc = base.unitsPerCarton;
  const outUnits = toUnits({ cartons: base.outCartons, units: 0 }, upc);
  const diff =
    toUnits(model.sold, upc) + toUnits(model.leftover, upc) + toUnits(model.damaged, upc) - outUnits;

  const withLeftover = hasQty(model.leftover);
  const rule = isDispositionCode(row.dispositionCode)
    ? DISPOSITION_RULES[row.dispositionCode]
    : null;

  /** 落點預覽（模組 `dispositionLocation()`；存檔後一律以後端回嘅 `toLoc` 為準） */
  const preview = isDispositionCode(row.dispositionCode)
    ? dispositionLocation(row.dispositionCode, {
        orderNo: row.targetOrderNo,
        storeCode: row.targetStoreCode,
      })
    : null;

  const placeholder = { value: '', label: t('recon.pickTarget') };
  const targetOptions =
    rule?.target === 'order'
      ? roadshowTargets.map((o) => ({
          value: o.orderNo,
          label: `${o.orderNo} · ${optionLabel(o, lang)}`,
        }))
      : storeTargets.map((s) => ({ value: s.code, label: `${s.code} · ${optionLabel(s, lang)}` }));

  const targetValue = rule?.target === 'order' ? row.targetOrderNo : row.targetStoreCode;
  const setTarget = (v: string) =>
    onChange(rule?.target === 'order' ? { targetOrderNo: v } : { targetStoreCode: v });

  return (
    <li style={{ padding: '10px 0', borderTop: '1px solid var(--color-divider)' }}>
      <strong>{base.itemName}</strong>
      <span style={hintStyle}> {base.barcode ?? ''}</span>
      {base.batchNo && <span style={hintStyle}> · {t('out.batch')}：{base.batchNo}</span>}
      <p style={hintStyle}>
        {t('recon.outCartons')}：{base.outCartons} {t('recon.cartons')}
        {' · '}
        {t('recon.declaredCartons')}：{base.declaredCartons}
        {base.pickedCartons !== null ? ` · ${t('out.picked')}：${base.pickedCartons}` : ''}
        {' · '}
        {t('recon.unitsPerCarton')}：{upc}
        {' · '}
        {t('recon.outUnits', { n: outUnits })}
      </p>

      <div style={{ display: 'grid', gap: '8px', marginTop: '6px' }}>
        <QtyGroup
          titleKey="recon.sold"
          lineId={base.lineId}
          group="sold"
          row={row}
          onChange={onChange}
          readOnly={readOnly}
        />
        <QtyGroup
          titleKey="recon.leftover"
          lineId={base.lineId}
          group="leftover"
          row={row}
          onChange={onChange}
          readOnly={readOnly}
        />
        <QtyGroup
          titleKey="recon.damaged"
          lineId={base.lineId}
          group="damaged"
          row={row}
          onChange={onChange}
          readOnly={readOnly}
        />
      </div>

      <p style={diff === 0 ? hintStyle : errText}>
        {diff === 0 ? t('recon.balanced') : t('recon.unbalanced', { n: diff })}
      </p>

      {withLeftover && (
        <div style={{ display: 'grid', gap: '8px', marginTop: '6px' }}>
          <FormSelectField
            id={`f-rc-disp-${base.lineId}`}
            labelKey="recon.disposition"
            value={row.dispositionCode}
            onChange={(v) =>
              // 換去向 ＝ 目標要重揀（清走舊目標，唔留上一手嘅單／店）
              onChange({ dispositionCode: v, targetOrderNo: '', targetStoreCode: '' })
            }
            options={[
              placeholder,
              ...dispositions.map((d) => ({ value: d.code, label: optionLabel(d, lang) })),
            ]}
            error={rule ? undefined : t('recon.dispositionRequired')}
          />
          {rule?.target && (
            <FormSelectField
              id={`f-rc-target-${base.lineId}`}
              labelKey={rule.target === 'order' ? 'recon.targetOrder' : 'recon.targetStore'}
              value={targetValue}
              onChange={setTarget}
              options={[placeholder, ...targetOptions]}
            />
          )}
          {preview && <p style={hintStyle}>{t('recon.toLocPreview', { loc: preview })}</p>}
          {rule && !rule.allowsUnits && <p style={hintStyle}>{t('recon.backToWarehouseHint')}</p>}
          <FormTextField
            id={`f-rc-note-${base.lineId}`}
            labelKey={rule?.needsNote ? 'recon.noteRequired' : 'recon.note'}
            value={row.note}
            readOnly={readOnly}
            onChange={(v) => onChange({ note: v ?? '' })}
          />
        </div>
      )}

      {issues.map((i) => (
        <p key={`${i.code}:${i.message}`} style={errText} role="alert">
          {i.message}
        </p>
      ))}
    </li>
  );
}
